import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { extractAuthors, showTemporaryMessage } from "./utils";
import { approveSection, disproveSection, updateSection } from "./api.js";
import { table_headers } from "./tableHeaders.js"
import ReviseFabTool from "./ReviseFabTool"; // Adjust the path as needed
import "./ProjectsWriting.css";
import "./ProjectEditing.css";

const CollapsibleText = (props, ref) => {
  const {
    isOpen,
    toggleContentKey,
    section_name,
    title,
    project_idx,
    setCheckboxes,
    checkboxes,
    maxChars,
    content_text = "",
    handleUpdateSectionData,
    refreshProjectData, 
    onCloseSection
  } = props;

  const structuredSections = ["RISKS", "PRIOR_WORK", "CONTRIBUTIONS", "SPECIFIC_TARGETS", "PRODUCTS"];

  const [text, setText] = useState(content_text);
  const [tableData, setTableData] = useState(() => {
    if (structuredSections.includes(section_name)) {
      if (typeof content_text === "string") {
        try {
          const parsed = JSON.parse(content_text);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      } else if (Array.isArray(content_text)) {
        //alert(JSON.stringify(content_text));
        return content_text;  // If content_text is already an array
      }
    }
    return [];
  });
  const [originalTableData, setOriginalTableData] = useState([]);
  const [originalText, setOriginalText] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const textareaRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (isOpen && textareaRef.current) {
      autoGrow(textareaRef.current);
    }
  }, [isOpen]);

  useEffect(() => {
    setText(content_text);
    if (structuredSections.includes(section_name)) {
      
      if (typeof content_text === "string") {
        try {
          const parsed = JSON.parse(content_text);
          setTableData(Array.isArray(parsed) ? parsed : []);
        } catch {
          setTableData([]);
        }
      } else if (Array.isArray(content_text)) {
        setTableData(content_text);
      }
    }
  }, [content_text, section_name]);

  const autoGrow = (element) => {
    element.style.height = "auto";
    element.style.height = element.scrollHeight + "px";
  };

  const handleTableChange = (index, key, value) => {
    const updated = [...tableData];
    updated[index][key] = value;
    setTableData(updated);
  };

  const handleAddRow = () => {
    let newRow = {};
    if (section_name === "RISKS") {
      newRow = { risk_definition: "", preventive_measures: "", probability: "", impact: "", plan_b: "" };
    } else if (section_name === "CONTRIBUTIONS") {
      newRow = { technique: "", develop: "", justification: "", wp: "" };
    } else if (section_name === "SPECIFIC_TARGETS") {
      newRow = { criterion: "", target: "" };
    } else if (section_name === "PRIOR_WORK") {
      newRow = { publication_title: "", date: "", authors: "", text: "" };
    }else if (section_name === "PRODUCTS") {
      newRow = {
        feature_name: "",
        list_product_explanations: [
          { product_name: "", explanation: "" }
        ]
      };
    }
    setTableData([...tableData, newRow]);
  };

  const handleRemoveRow = (index) => {
    const updated = [...tableData];
    updated.splice(index, 1);
    setTableData(updated);
  };

  const handleSave = async (section_name) => {
    setIsEditing(false);

    const new_text = structuredSections.includes(section_name) ? tableData : text;

    const res = await updateSection(project_idx, section_name, new_text);
    const status = res.status
    const message = res.message

    if (status === "SUCCESS") {
      showTemporaryMessage("Başarıyla kaydedildi ✅");

      const updatedTextForParent = structuredSections.includes(section_name)
        ? JSON.stringify(tableData)
        : text;

      handleUpdateSectionData(section_name, updatedTextForParent);

      if (refreshProjectData) {
        await refreshProjectData();
      }

      if (structuredSections.includes(section_name)) {
        setText(updatedTextForParent);  // Sync local state
      }
    } else {
      showTemporaryMessage("Kaydetme başarısız ❌");
      alert(message);
    }
  };

  const handleEdit = () => {
  if (isEditing) {
    // Cancel edit: revert changes
    if (structuredSections.includes(section_name)) {
      setTableData(originalTableData); // restore structured data
    } else {
      setText(originalText); // restore plain text
    }
    setIsEditing(false);
  } else {
    // Enter edit mode
    if (structuredSections.includes(section_name)) {
      setOriginalTableData(JSON.parse(JSON.stringify(tableData))); // deep copy
    } else {
      setOriginalText(text); // save plain text
    }
    handleDisprove();
    setIsEditing(true);
  }
  };

  const handleRevise = () => {
    setIsEditing(false);
    handleDisprove();
  };

  const handleDisprove = async () => {
    const status = await disproveSection(project_idx, section_name);
    setCheckboxes({ ...checkboxes, [section_name]: false });
  };

  const handleApprove = async () => {
    const status = await approveSection(project_idx, section_name);
    if (status === "SUCCESS") {
      setCheckboxes({ ...checkboxes, [section_name]: true });
      showTemporaryMessage("Onay başarılı ✅");
      
      // if (onCloseSection) {
      //   onCloseSection(section_name);
      // }
    } else {
      showTemporaryMessage("Onay başarısız ❌");
    }
  };

  const renderTable = () => {
    const commonRowActions = (index) => (
      isEditing && (
        <td className="table-actions-cell">
          <button onClick={() => handleRemoveRow(index)} className="delete-btn small">❌</button>
        </td>
      )
    );

    if (section_name === "RISKS") {
      return (
        <>
          <table className="risks-table">
            <thead>
              <tr>
                <th>{table_headers["risk_definition"]}</th>
                <th>{table_headers["preventive_measures"]}</th>
                <th>{table_headers["probability"]}</th>
                <th>{table_headers["impact"]}</th>
                <th>{table_headers["plan_b"]}</th>
                {isEditing && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tableData.map((risk, index) => (
                <tr key={index}>
                  <td>{isEditing ? <textarea value={risk.risk_definition || ""} onChange={(e) => {handleTableChange(index, "risk_definition", e.target.value); autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : risk.risk_definition || "—"}</td>
                  <td>{isEditing ? <textarea value={risk.preventive_measures || ""} onChange={(e) => {handleTableChange(index, "preventive_measures", e.target.value); autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : risk.preventive_measures || "—"}</td>
                  <td>{isEditing ? <input type="text" value={risk.probability || ""} onChange={(e) => handleTableChange(index, "probability", e.target.value)} /> : risk.probability || "—"}</td>
                  <td>{isEditing ? <input type="text" value={risk.impact || ""} onChange={(e) => handleTableChange(index, "impact", e.target.value)} /> : risk.impact || "—"}</td>
                  <td>{isEditing ? <textarea value={risk.plan_b || ""} onChange={(e) => {handleTableChange(index, "plan_b", e.target.value); autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : risk.plan_b || "—"}</td>
                  {commonRowActions(index)}
                </tr>
              ))}
            </tbody>
          </table>
          {isEditing && <button onClick={handleAddRow} className="add-row-btn">➕ Satır Ekle</button>}
        </>
      );
    } else if (section_name === "CONTRIBUTIONS") {
      return (
        <>
          <table className="contributions-table">
            <thead>
              <tr>
                <th>{table_headers["technique"]}</th>
                <th>{table_headers["develop"]}</th>
                <th>{table_headers["justification"]}</th>
                <th>{table_headers["wp"]}</th>
                {isEditing && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tableData.map((tech, index) => (
                <tr key={index}>
                  <td>{isEditing ? <textarea value={tech.technique || ""} onChange={(e) => {handleTableChange(index, "technique", e.target.value); autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : tech.technique || "—"}</td>
                  <td>{isEditing ? <input type="text" value={tech.develop || ""} onChange={(e) => handleTableChange(index, "develop", e.target.value)} /> : tech.develop || "—"}</td>
                  <td>{isEditing ? <textarea value={tech.justification || ""} onChange={(e) => {handleTableChange(index, "justification", e.target.value); autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : tech.justification || "—"}</td>
                  <td>{isEditing ? <textarea value={tech.wp || ""} onChange={(e) => {handleTableChange(index, "wp", e.target.value); autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : tech.wp || "—"}</td>
                  {commonRowActions(index)}
                </tr>
              ))}
            </tbody>
          </table>
          {isEditing && <button onClick={handleAddRow} className="add-row-btn">➕ Satır Ekle</button>}
        </>
      );
    } else if (section_name === "SPECIFIC_TARGETS") {
      return (
        <>
          <table className="specific_targets-table">
            <thead>
              <tr>
                <th>{table_headers["criterion"]}</th>
                <th>{table_headers["target"]}</th>
                {isEditing && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tableData.map((target, index) => (
                <tr key={index}>
                  <td>{isEditing ? <textarea value={target.criterion || ""} onChange={(e) => handleTableChange(index, "criterion", e.target.value)} /> : target.criterion  || "—"}</td>
                  <td>{isEditing ? <textarea value={target.target || ""} onChange={(e) => handleTableChange(index, "target", e.target.value)} /> : target.target  || "—"}</td>
                  {commonRowActions(index)}
                </tr>
              ))}
            </tbody>
          </table>
          {isEditing && <button onClick={handleAddRow} className="add-row-btn">➕ Satır Ekle</button>}
        </>
      );
    } else if (section_name === "PRIOR_WORK") {
      return (
        <>
          <table className="prior_work-table">
            <thead>
              <tr>
                <th>{table_headers["publication_title"]}</th>
                <th>{table_headers["date"]}</th>
                <th>{table_headers["authors"]}</th>
                <th>{table_headers["text"]}</th>
                {isEditing && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tableData.map((pub, index) => (
                <tr key={index}>
                  <td>{isEditing ? <textarea value={pub.publication_title || ""} onChange={(e) => {handleTableChange(index, "publication_title", e.target.value);  autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : pub.publication_title || "—"}</td>
                  <td>{isEditing ? <textarea value={pub.date || ""} onChange={(e) => {handleTableChange(index, "date", e.target.value);  autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : pub.date || "—"}</td>
                  <td>{isEditing ? <textarea value={pub.authors || ""} onChange={(e) => {handleTableChange(index, "authors", e.target.value);  autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : pub.authors || "—"}</td>
                  <td>{isEditing ? <textarea value={pub.text || ""} onChange={(e) => {handleTableChange(index, "text", e.target.value);  autoGrow(e.target);}} ref={(el) => el && autoGrow(el)} /> : pub.text || "—"}</td>
                  {commonRowActions(index)}
                </tr>
              ))}
            </tbody>
          </table>
          {isEditing && <button onClick={handleAddRow} className="add-row-btn">➕ Satır Ekle</button>}
        </>
      );
    }else if (section_name === "PRODUCTS") {
      // Step 1: Extract unique product names (from the first row)
      const allProductNames = [
        ...new Set(
          tableData.flatMap((row) =>
            row.list_product_explanations?.map((item) => item.product_name) || []
          )
        )
      ];

      // Step 2: Build the table as a matrix
      return (
        <>
         {/*<p>Bu bölüm "beta" sürümünde olup ön kullanıma açılmıştır.</p>*/}
          <table className="products-matrix-table">
            <thead>
              <tr>
                <th>Teknik<br/>Özellik</th>
                {allProductNames.map((name, i) => (
                  <th key={i}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span>{name === "project_outcome" ? "Proje Çıktısı" : name}</span>
                      {isEditing && (
                        <button
                          onClick={() => {
                            // Remove this column's product name from all rows
                            const updatedData = tableData.map((row) => {
                              const filtered = row.list_product_explanations.filter(
                                (item) => item.product_name !== name
                              );
                              return {
                                ...row,
                                list_product_explanations: filtered
                              };
                            });
                            setTableData(updatedData);
                          }}
                          className="delete-btn small"
                          title={`'${name}' ürününü kaldır`}
                          style={{ marginLeft: 4 }}
                        >
                          ❌
                        </button>
                      )}
                    </div>
                  </th>
                ))}
                {isEditing && <th></th>}
              </tr>
            </thead>
            <tbody>
              {tableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                <td>
                  {isEditing ? (
                    <textarea
                      value={row.feature_name || ""}
                      onChange={(e) => handleTableChange(rowIndex, "feature_name", e.target.value)}
                      rows={2}
                      style={{ width: "100%", resize: "vertical" }}
                    />
                  ) : (
                    row.feature_name
                  )}
                </td>
                  {allProductNames.map((productName, colIndex) => {
                    const match = row.list_product_explanations?.find(
                      (item) => item.product_name === productName
                    );
                    const value = match?.explanation || "";

                    return (
                      <td key={colIndex}>
                        {isEditing ? (
                          <textarea
                            value={value}
                            onChange={(e) => {
                              const updatedRow = [...row.list_product_explanations];
                              const idx = updatedRow.findIndex((item) => item.product_name === productName);
                              if (idx >= 0) {
                                updatedRow[idx].explanation = e.target.value;
                              } else {
                                updatedRow.push({
                                  product_name: productName,
                                  explanation: e.target.value
                                });
                              }
                              handleTableChange(rowIndex, "list_product_explanations", updatedRow);
                            }}
                          />
                        ) : (
                          value || "—"
                        )}
                      </td>
                    );
                  })}
                  {isEditing && (
                    <td>
                      <button onClick={() => handleRemoveRow(rowIndex)} className="delete-btn small">❌</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          {isEditing && (
            <button onClick={handleAddRow} className="add-row-btn">
              ➕ Satır Ekle
            </button>
          )}
        </>
      );
    }

    // Other sections like PRIOR_WORK and SPECIFIC_TARGETS remain unchanged
    // Add them similarly if needed.

    return null;
  };

  

  return (
    <div className="text-section" ref={ref}>
      <div className="text-header" onClick={() => toggleContentKey(section_name, !isOpen)}>
        <h4>{title}</h4>
        <button className="toggle-btn">{isOpen ? "▼" : "►"}</button>
      </div>
      {isOpen && (
        <div className="text-content">
          {structuredSections.includes(section_name)
            ? renderTable()
            : (
              <textarea
                ref={textareaRef}
                style={{ height: "auto" }}
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  autoGrow(e.target);
                }}
                disabled={!isEditing}
              />
            )}

            {maxChars && !structuredSections.includes(section_name) && (
              <p style={{ textAlign: "right" }}>
                {text.length} / {maxChars} karakter
              </p>
            )}
 
          <div className="btn-group">
            <button className="edit-btn" onClick={handleEdit}>  {isEditing ? "VAZGEÇ" : "DÜZELT (+AI)"}</button>
            <button className="save-btn" onClick={() => handleSave(section_name)} disabled={!isEditing}>KAYDET</button>
            {/* <button className="revise-btn" onClick={handleRevise}>REVİZE ET (AI)</button> */}
            <button className="approve-btn" onClick={handleApprove}>ONAYLA</button>
          </div>
              <ReviseFabTool
              project_idx={project_idx}
              section_name={section_name}
              isEditing={isEditing}
              setIsEditing={setIsEditing}
              title={title}
              originalContent={
                structuredSections.includes(section_name)
                  ? JSON.stringify(tableData)
                  : text
              }
              onApply={(aiContent) => {
                if (structuredSections.includes(section_name)) {
                  try {
                    const parsed = JSON.parse(aiContent);
                    setTableData(Array.isArray(parsed) ? parsed : []);
                  } catch {
                    showTemporaryMessage("Geçersiz AI çıktısı ❌");
                  }
                } else {
                  setText(aiContent);
                }
              }}
            />
        </div>
      )}
    </div>
  );
};

export default React.forwardRef(CollapsibleText);
